// ./src/components/features/Tasks/TaskEditDrawer/TaskEditDrawer.tsx
//
// Create/edit drawer for an event task / appointment. Self-loading in edit mode via
// recordId; context-or-props resolution; no success toast (the page highlight is the
// confirmation). Fields (single per line): type, title, category, scheduled date/time
// (in the event's timezone), expected duration, who is responsible, description
// (markdown), status. In EDIT mode a progress panel on top drives the time-tracking
// transitions (start / pause / complete / reopen). `eventId` is set on CREATE only.
// Mirrors PoiEditDrawer.

import React, { useState, useEffect, useCallback, useMemo, useRef, Suspense } from "react";
import { Stack, TextInput, NumberInput, Input, Skeleton, SegmentedControl, Group, Box, Text } from "@mantine/core";

import { OpResult } from "@sdflc/api-helpers";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import { Task, TaskInput, TASK_TYPE, TASK_LOCATION_KIND, TaskTransitionAction } from "@/utils/api";
import { getFormErrors, formatErrors } from "@/utils";
import { useModalShortcuts, SHORTCUT_HINTS } from "@/hooks";
import { useOptionalTasksContext } from "@/contexts/TasksContext";
import { AppDrawer, CategorySelect, DateTimeField } from "@/components/generic";

import { ResponsiblePartySelect } from "../ResponsiblePartySelect/ResponsiblePartySelect";
import { TaskLocationFields, TaskLocationValue } from "../TaskLocationFields/TaskLocationFields";
import { TaskProgress } from "../TaskProgress/TaskProgress";
import { getTaskTypeLabel } from "../taskHelpers";

dayjs.extend(utc);
dayjs.extend(timezone);

// MarkdownField pulls in a heavy CodeMirror bundle — lazy-load it by path.
const MarkdownField = React.lazy(() =>
  import("@/components/generic/MarkdownField/MarkdownField").then((m) => ({ default: m.MarkdownField }))
);

const resolveTz = (tz?: string | null): string => {
  if (tz) return tz;
  try {
    return dayjs.tz.guess();
  } catch {
    return "UTC";
  }
};

// UTC (stored) ⇄ the event-timezone local string the picker edits.
const utcToZoned = (utcStr?: string | null, tz?: string | null): string | null => {
  if (!utcStr) return null;
  const d = dayjs.utc(utcStr);
  if (!d.isValid()) return null;
  return d.tz(resolveTz(tz)).format("YYYY-MM-DD HH:mm:ss");
};

const zonedToUtc = (localStr?: string | null, tz?: string | null): string | null => {
  if (!localStr) return null;
  const d = dayjs.tz(localStr, resolveTz(tz));
  if (!d.isValid()) return null;
  return d.utc().toISOString();
};

export interface TaskEditDrawerProps {
  opened: boolean;
  onClose: () => void;
  recordId?: string | null;
  /** The event this task belongs to — set on the input in CREATE mode only. */
  eventId?: string | null;
  /** IANA timezone of the event; the schedule field is edited in this zone. */
  eventTimezone?: string | null;
  /** A title for a NEW task (the create wizard's one-tap starters, "Book the venue"). Ignored when editing. */
  defaultTitle?: string | null;
  onCreated?: (task: Task) => void;
  onUpdated?: (task: Task) => void;
}

interface FormData {
  taskType: string;
  title: string;
  categoryId: string | null;
  scheduledAtLocal: string | null;
  durationHours: number | "";
  durationMinutes: number | "";
  responsibleKind: string | null;
  responsibleId: string | null;
  locationKind: string | null;
  locationVenueId: string | null;
  locationAddress: string;
  locationLat: number | null;
  locationLng: number | null;
  onlineMeetingUrl: string;
  description: string;
}

const KNOWN_FORM_FIELDS = [
  "taskType",
  "title",
  "categoryId",
  "scheduledAt",
  "expectedDurationMin",
  "responsibleKind",
  "responsibleId",
  "locationKind",
  "locationVenueId",
  "locationAddress",
  "onlineMeetingUrl",
  "description",
];

const getInitialFormData = (defaultTitle?: string | null): FormData => ({
  taskType: TASK_TYPE.TASK,
  title: defaultTitle ?? "",
  categoryId: null,
  scheduledAtLocal: null,
  durationHours: "",
  durationMinutes: "",
  responsibleKind: null,
  responsibleId: null,
  locationKind: null,
  locationVenueId: null,
  locationAddress: "",
  locationLat: null,
  locationLng: null,
  onlineMeetingUrl: "",
  description: "",
});

const fromRecord = (record: Task, tz?: string | null): FormData => {
  const total = Number(record.expectedDurationMin) || 0;

  return {
    taskType: record.taskType || TASK_TYPE.TASK,
    title: record.title || "",
    categoryId: record.categoryId || null,
    scheduledAtLocal: utcToZoned(record.scheduledAt, tz),
    durationHours: total >= 60 ? Math.floor(total / 60) : total > 0 ? 0 : "",
    durationMinutes: total > 0 ? total % 60 : "",
    responsibleKind: record.responsibleKind || null,
    responsibleId: record.responsibleId || null,
    locationKind: record.locationKind || null,
    locationVenueId: record.locationVenueId || null,
    locationAddress: record.locationAddress || "",
    locationLat: record.latitude ?? null,
    locationLng: record.longitude ?? null,
    onlineMeetingUrl: record.onlineMeetingUrl || "",
    description: record.description || "",
  };
};

const serialize = (data: FormData): string => JSON.stringify(data);

const toExpectedMin = (data: FormData): number | null => {
  const h = data.durationHours === "" ? 0 : Number(data.durationHours);
  const m = data.durationMinutes === "" ? 0 : Number(data.durationMinutes);
  const total = h * 60 + m;
  return total > 0 ? total : null;
};

const TaskEditDrawerSkeleton: React.FC = () => (
  <Stack gap="md">
    <Skeleton height={40} radius="sm" />
    <Skeleton height={60} radius="sm" />
    <Skeleton height={60} radius="sm" />
    <Skeleton height={120} radius="sm" />
  </Stack>
);

export const TaskEditDrawer: React.FC<TaskEditDrawerProps> = ({
  opened,
  onClose,
  recordId,
  eventId,
  eventTimezone,
  defaultTitle,
  onCreated,
  onUpdated,
}) => {
  const ctx = useOptionalTasksContext();
  const loadFn = ctx?.get;
  const createFn = ctx?.create;
  const updateFn = ctx?.update;
  const transitionFn = ctx?.transition;
  const creating = ctx?.isCreating ?? false;
  const updating = ctx?.isUpdating ?? false;
  const operationError = ctx?.lastError ?? null;
  const clearOperationError = ctx?.clearError;

  const isEditMode = Boolean(recordId);
  const isSubmitting = creating || updating;

  // Client-side "an appointment needs a date/time" error.
  const [scheduleError, setScheduleError] = useState<string | null>(null);

  const loadFnRef = useRef(loadFn);
  loadFnRef.current = loadFn;
  const clearErrRef = useRef(clearOperationError);
  clearErrRef.current = clearOperationError;

  // ── Self-loading ──
  const [record, setRecord] = useState<Task | null>(null);
  const [isLoadingRecord, setIsLoadingRecord] = useState(false);
  const [loadError, setLoadError] = useState<OpResult | null>(null);

  const showLoadingSkeleton = isLoadingRecord && !record;
  // An edit drawer WITHOUT its record (the read failed) shows the error only — its form still held the last record's
  // values, and Save wrote them over this one (#131 review). Save and ⌘S are off until the record is read.
  const withoutRecord = isEditMode && !record;

  useEffect(() => {
    let isCancelled = false;

    if (opened && isEditMode && recordId && loadFnRef.current) {
      setIsLoadingRecord(true);
      setRecord(null);
      setLoadError(null);

      loadFnRef.current(recordId).then((result) => {
        if (isCancelled) {
          return;
        }

        if (result.didSucceed()) {
          setRecord(result.getDataFirst() as Task);
          setLoadError(null);
        } else {
          setRecord(null);
          setLoadError(result);
        }

        setIsLoadingRecord(false);
      });
    } else if (!opened) {
      setRecord(null);
      setIsLoadingRecord(false);
      setLoadError(null);
    }

    return () => {
      isCancelled = true;
    };
  }, [opened, isEditMode, recordId]);

  // ── Form state ──
  const [formData, setFormData] = useState<FormData>(() => getInitialFormData());
  const initialSnapshotRef = useRef<string>("");
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!opened || showLoadingSkeleton || (isEditMode && !record)) {
      // Not dirty while closed, loading, or without its record (a failed read) — a stale flag from the last visit
      // asked "Discard changes?" over nothing (#131).
      setIsDirty(false);

      return;
    }

    setIsDirty(serialize(formData) !== initialSnapshotRef.current);
  }, [formData, opened, showLoadingSkeleton, isEditMode, record]);

  const effectiveError = loadError || operationError;

  const { fields: fieldErrors, genericErrors } = useMemo(
    () => getFormErrors(effectiveError, KNOWN_FORM_FIELDS),
    [effectiveError]
  );

  const formattedGenericError = useMemo(() => formatErrors(genericErrors), [genericErrors]);

  // "Where" is ONE composite control (kind + venue | address | meeting URL), so its
  // per-key refusals fold into that block's own error — the same precedent as
  // latitude / longitude folding into a location block. Folding still READS the
  // key, which is what keeps a listed-but-unwired name from being swallowed.
  const locationError =
    fieldErrors.locationKind ||
    fieldErrors.locationVenueId ||
    fieldErrors.locationAddress ||
    fieldErrors.onlineMeetingUrl;

  useEffect(() => {
    if (!opened) {
      return;
    }

    if (isEditMode && !record) {
      return;
    }

    const newFormData = record ? fromRecord(record, eventTimezone) : getInitialFormData(defaultTitle);
    setFormData(newFormData);
    initialSnapshotRef.current = serialize(newFormData);
    setIsDirty(false);
    setScheduleError(null);
    clearErrRef.current?.();
  }, [opened, record, isEditMode, eventTimezone, defaultTitle]);

  const setField = useCallback((field: keyof FormData, value: FormData[keyof FormData]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleResponsibleChange = useCallback((kind: string | null, id: string | null) => {
    setFormData((prev) => ({ ...prev, responsibleKind: kind, responsibleId: id }));
  }, []);

  const locationValue: TaskLocationValue = {
    kind: formData.locationKind,
    venueId: formData.locationVenueId,
    address: formData.locationAddress,
    latitude: formData.locationLat,
    longitude: formData.locationLng,
    url: formData.onlineMeetingUrl,
  };

  const handleLocationChange = useCallback((next: TaskLocationValue) => {
    setFormData((prev) => ({
      ...prev,
      locationKind: next.kind,
      locationVenueId: next.venueId,
      locationAddress: next.address,
      locationLat: next.latitude,
      locationLng: next.longitude,
      onlineMeetingUrl: next.url,
    }));
  }, []);

  // The freshest copy of the task for the progress panel (transitions update the
  // shared list, not this drawer's loaded snapshot).
  const liveTask = (recordId ? ctx?.getById(recordId) : null) ?? record;
  const transitioning = recordId ? ctx?.isTransitioning(recordId) ?? false : false;

  const handleTransition = useCallback(
    (action: TaskTransitionAction) => {
      if (recordId && transitionFn) {
        transitionFn(recordId, action);
      }
    },
    [recordId, transitionFn]
  );

  // ── Submit ──
  const handleSubmit = useCallback(async () => {
    clearErrRef.current?.();
    setLoadError(null);

    // An appointment must have a date/time (server enforces it too).
    if (formData.taskType === TASK_TYPE.APPOINTMENT && !formData.scheduledAtLocal) {
      setScheduleError("An appointment needs a date and time");
      return;
    }
    setScheduleError(null);

    const submitData: TaskInput = {
      taskType: formData.taskType,
      title: formData.title.trim() || null,
      categoryId: formData.categoryId,
      scheduledAt: zonedToUtc(formData.scheduledAtLocal, eventTimezone),
      expectedDurationMin: toExpectedMin(formData),
      responsibleKind: formData.responsibleKind,
      responsibleId: formData.responsibleId,
      locationKind: formData.locationKind,
      locationVenueId: formData.locationKind === TASK_LOCATION_KIND.VENUE ? formData.locationVenueId : null,
      locationAddress:
        formData.locationKind === TASK_LOCATION_KIND.ADDRESS ? formData.locationAddress.trim() || null : null,
      latitude: formData.locationKind === TASK_LOCATION_KIND.ADDRESS ? formData.locationLat : null,
      longitude: formData.locationKind === TASK_LOCATION_KIND.ADDRESS ? formData.locationLng : null,
      onlineMeetingUrl:
        formData.locationKind === TASK_LOCATION_KIND.ONLINE ? formData.onlineMeetingUrl.trim() || null : null,
      description: formData.description.trim() || null,
    };

    if (!isEditMode) {
      submitData.eventId = eventId ?? null;
    }

    let result: OpResult | null = null;
    let isCreate = false;

    if (isEditMode && recordId && updateFn) {
      result = await updateFn(recordId, submitData);
    } else if (!isEditMode && createFn) {
      result = await createFn(submitData);
      isCreate = true;
    }

    if (result?.didSucceed()) {
      setIsDirty(false);
      const saved = result.data ? (result.data as Task[])[0] : null;

      if (saved) {
        if (isCreate) {
          onCreated?.(saved);
        } else {
          onUpdated?.(saved);
        }
      }

      onClose();
    }
  }, [formData, isEditMode, recordId, eventId, eventTimezone, createFn, updateFn, onClose, onCreated, onUpdated]);

  useModalShortcuts({
    onConfirm: handleSubmit,
    canConfirm: !isSubmitting && !showLoadingSkeleton && !withoutRecord,
    enabled: opened,
  });

  const drawerTitle = isEditMode ? "Edit task" : "Add task";
  const tzLabel = resolveTz(eventTimezone);

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={drawerTitle}
      position="right"
      size="md"
      closable={!isSubmitting}
      loading={isSubmitting}
      errors={formattedGenericError}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean)}
      actionType="save-cancel"
      onConfirm={handleSubmit}
      confirmShortcut={SHORTCUT_HINTS.save}
      confirmDisabled={showLoadingSkeleton || withoutRecord}
      isDirty={isDirty}
    >
      {withoutRecord && !showLoadingSkeleton ? null : showLoadingSkeleton ? (
        <TaskEditDrawerSkeleton />
      ) : (
        <Stack gap="md">
          {isEditMode && liveTask ? (
            <Box
              style={{
                padding: "var(--mantine-spacing-sm)",
                borderRadius: "var(--mantine-radius-md)",
                border: "1px solid var(--mantine-color-default-border)",
                background: "var(--mantine-color-body)",
              }}
            >
              <TaskProgress task={liveTask} onTransition={handleTransition} transitioning={transitioning} />
            </Box>
          ) : null}

          <Input.Wrapper error={fieldErrors.taskType}>
            <SegmentedControl
              fullWidth
              value={formData.taskType}
              onChange={(v) => setField("taskType", v)}
              data={[
                { value: TASK_TYPE.TASK, label: getTaskTypeLabel(TASK_TYPE.TASK) },
                { value: TASK_TYPE.APPOINTMENT, label: getTaskTypeLabel(TASK_TYPE.APPOINTMENT) },
              ]}
            />
          </Input.Wrapper>

          <TextInput
            label="Title"
            placeholder="What needs to be done?"
            value={formData.title}
            onChange={(e) => setField("title", e.currentTarget.value)}
            error={fieldErrors.title}
            required
            data-autofocus
            size="md"
          />

          <CategorySelect
            group="task"
            label="Category"
            description="Group similar work (setup, logistics, coordination…)."
            value={formData.categoryId}
            onChange={(id) => setField("categoryId", id)}
            error={fieldErrors.categoryId}
            clearable
            size="md"
          />

          <DateTimeField
            label={formData.taskType === TASK_TYPE.APPOINTMENT ? "When" : "Due"}
            description={`Shown in the event's timezone (${tzLabel})`}
            value={formData.scheduledAtLocal}
            onChange={(_name, v) => {
              setField("scheduledAtLocal", v);
              if (v) setScheduleError(null);
            }}
            error={scheduleError || fieldErrors.scheduledAt}
            required={formData.taskType === TASK_TYPE.APPOINTMENT}
            clearable
            size="md"
          />

          <Input.Wrapper label="Expected duration" error={fieldErrors.expectedDurationMin}>
            <Group gap="xs" grow align="flex-start">
              <NumberInput
                aria-label="Hours"
                placeholder="0"
                value={formData.durationHours}
                onChange={(v) => setField("durationHours", v === "" ? "" : (v as number))}
                min={0}
                max={1000}
                suffix=" h"
                size="md"
              />
              <NumberInput
                aria-label="Minutes"
                placeholder="0"
                value={formData.durationMinutes}
                onChange={(v) => setField("durationMinutes", v === "" ? "" : (v as number))}
                min={0}
                max={59}
                suffix=" m"
                size="md"
              />
            </Group>
          </Input.Wrapper>

          <ResponsiblePartySelect
            eventId={eventId || record?.eventId || ""}
            value={{ kind: formData.responsibleKind, id: formData.responsibleId }}
            onChange={handleResponsibleChange}
            fallbackLabel={record?.responsibleLabel}
            label={formData.taskType === TASK_TYPE.APPOINTMENT ? "Who's meeting (our side)" : "Who is responsible"}
            kindError={fieldErrors.responsibleKind}
            idError={fieldErrors.responsibleId}
          />

          {formData.taskType === TASK_TYPE.APPOINTMENT ? (
            <TaskLocationFields
              value={locationValue}
              onChange={handleLocationChange}
              fallbackVenueLabel={record?.locationLabel}
              error={locationError}
            />
          ) : locationError ? (
            // A plain task renders no "Where" block, so a location refusal would
            // have nowhere to land. Show it rather than let it vanish.
            <Text fz="sm" c="red">
              {locationError}
            </Text>
          ) : null}

          <Input.Wrapper label="Description" error={fieldErrors.description}>
            <Suspense fallback={<Skeleton height={160} radius="sm" />}>
              <MarkdownField
                value={formData.description}
                onChange={(md) => setField("description", md)}
                placeholder="Notes, checklist, details…"
                minHeight={160}
              />
            </Suspense>
          </Input.Wrapper>

          {!isEditMode ? (
            <Text fz="sm" c="dimmed">
              Track time with Start / Pause / Complete once the task is created.
            </Text>
          ) : null}
        </Stack>
      )}
    </AppDrawer>
  );
};

export default TaskEditDrawer;
